/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getCartState, ICartState } from '@msdyn365-commerce/global-state';
import { ICache, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { getSelectedVariant, SelectedVariantInput } from '@msdyn365-commerce-modules/retail-actions';
import crypto from 'crypto';
import promisify from 'util.promisify';
import { IGetExperlogixStateData, IExperlogixWebConfiguratorData } from '../experlogix-d365commerce-core';
import {
    getSelectedCartLineIdFromActionInput,
    getSelectedProductIdFromActionInput
} from '../experlogix-d365commerce-core/commerceUtilities';
import { IExperlogixWebConfiguratorConfig } from '../modules/experlogix-web-configurator/experlogix-web-configurator.props.autogenerated';
import getSeriesModel, { GetProductExperlogixSeriesModelInput } from './get-product-experlogix-seriesmodel.action';
import { ICoreContext } from '@msdyn365-commerce/core';

/**
 * ExtensionProperty specs for the authorization nonce token
 */
const authNonceProperty = 'ExperlogixAuthorization';
const authNonceBitStrength = 1024;
const cacheObjectType = 'IGetExperlogixStateData';
const cacheKey = 'ExperlogixState';

/**
 * GetExperlogixState Input Action
 */
export class GetExperlogixStateInput implements Msdyn365.IActionInput {
    public ExperlogixSiteUrl: string;
    public SelectedProductId?: number;
    public SelectedCartLineId?: string;

    constructor(experlogixSiteUrl: string, selectedProductId?: number | string, selectedCartLineId?: string) {
        if (!experlogixSiteUrl) {
            throw new Error(`GetExperlogixStateInput: experlogixSiteUrl must be specified`);
        }

        // Validates that the URL is correctly structured
        try {
            new URL(experlogixSiteUrl);
        } catch (error) {
            throw new Error(`GetExperlogixStateInput: experlogixSiteUrl must be valid.  Error: ${error}`);
        }

        this.ExperlogixSiteUrl = experlogixSiteUrl;
        this.SelectedProductId = selectedProductId ? +selectedProductId : undefined;
        this.SelectedCartLineId = selectedCartLineId;
    }

    // Cache definition should limit this aggregation of states to
    // special cases, or not at all.
    public getCacheKey = () => cacheKey;
    public getCacheObjectType = () => cacheObjectType;
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

/**
 * Used for on-load state preparation and inclusion with registered modules
 */
const createInput = (
    args: Msdyn365.ICreateActionContext<IExperlogixWebConfiguratorConfig, IExperlogixWebConfiguratorData>
): Msdyn365.IActionInput => {
    if (args.requestContext.app.config?.experlogixSiteUrl) {
        return new GetExperlogixStateInput(
            args.requestContext.app.config.experlogixSiteUrl,
            getSelectedProductIdFromActionInput(args),
            getSelectedCartLineIdFromActionInput(args)
        );
    } else {
        throw new Error(`get-experlogix-state cannot operate without an Experlogix configuration.`);
    }
};

/**
 * Establish a promise wrapper for the crypto method call, for async/await use.
 */
const randomBytesAsync = promisify(crypto.randomBytes);

async function getProductVariantAsync(productId: number, ctx: Msdyn365.IActionContext): Promise<SimpleProduct | null> {
    const apiSettings = ctx.requestContext.apiSettings;
    let product: SimpleProduct | null = null;
    product = await getSelectedVariant(new SelectedVariantInput(productId, +apiSettings.channelId), ctx);
    return product;
}

export function getActionContext(context: ICoreContext): Msdyn365.ActionContext {
    const actionContext = new Msdyn365.ActionContext({
        id: '',
        requestContext: context.actionContext.requestContext as any,
        requestCache: (context.actionContext as any).requestCache as ICache,
        appCache: null
    });
    return actionContext;
}

/**
 * Determines if a product is configurable or not
 */
async function isConfigurableAsync(ExperlogixSiteUrl: string, productItemId: string, ctx: Msdyn365.IActionContext): Promise<boolean> {
    const seriesModelConstraints = await getSeriesModel(new GetProductExperlogixSeriesModelInput(ExperlogixSiteUrl, productItemId), ctx);

    // A truthy evaluation of the response is suitable for asserting that some constraint
    // is specified, and therefore the product is configurable by convention.
    return !!seriesModelConstraints;
}

async function setCartTokenAsync(cartState: ICartState) {
    // Generate randomized token for nonce-based authorization
    const authNonce = (await randomBytesAsync(authNonceBitStrength / 8)).toString('hex');

    // Add the token to the cart
    await cartState.updateExtensionProperties({
        newExtensionProperties: [{ Key: authNonceProperty, Value: { StringValue: authNonce } }]
    });

    return {
        cartState: cartState,
        authNonce: authNonce
    };
}

export async function getProductStateAsync(input: GetExperlogixStateInput, ctx: Msdyn365.IActionContext) {
    let authNonce = '';
    let isProductConfigurable = false;
    let product: SimpleProduct | null = null;
    const cartState = await getCartState(ctx);

    if (input.SelectedProductId) {
        product = await getProductVariantAsync(input.SelectedProductId, ctx);

        if (product?.ItemId) {
            isProductConfigurable = await isConfigurableAsync(input.ExperlogixSiteUrl, product.ItemId, ctx);
        }
    }

    // If the product is configurable, provide authorization to CPQ's Commerce integrator
    if (isProductConfigurable) {
        const cartToken = await setCartTokenAsync(cartState);
        authNonce = cartToken.authNonce;
    }

    return {
        authNonce,
        cart: cartState.cart,
        cartLineId: input.SelectedCartLineId || null,
        product: product
    };
}

/**
 * Assembles all of the state necessary for Experlogix component operation.  No caching
 * is performed for this data action; use of other actions with caching/batching semantics
 * is preferrable.
 */
export async function action(input: GetExperlogixStateInput, ctx: Msdyn365.IActionContext): Promise<IGetExperlogixStateData> {
    const result = await getProductStateAsync(input, ctx);
    return result;
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetExperlogixStateData>>action,
    id: 'GetExperlogixState',
    input: createInput
});
